import React from 'react';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';

// downshift
import {
  useCombobox,
  UseComboboxProps,
  UseComboboxState,
  UseComboboxStateChangeOptions,
  useMultipleSelection,
} from 'downshift';
import Text from '../text';
// components
import Icon, { IconAnimatedChevron } from '../icon';
import { RoundButton } from '../button';

// style
import Spinner from '../spinner';
import { dropdownVariants, MultiSelectionComponentWrapper } from './style';

export interface MultiComboboxProps<T = unknown> {
  items: T[];
  alwaysOpen?: boolean;
  onChange?: (items?: T[]) => void;
  placeholder?: string;
  isItemDisabled?: (item: T) => boolean;
  isAsync?: boolean;
  renderItem?: (item: T) => React.JSX.Element | string;
  value?: T[];
  menuMode?: 'static' | 'absolute';
  isItemSelected?: (selectedItems: T[], item: T | null | undefined) => boolean;
  disabled?: boolean;
  onItemSelect?: (item: T | null | undefined) => void;
  onClear?: () => void;
  active?: boolean;
  /* must include a null check https://github.com/downshift-js/downshift/tree/master/src/hooks/useCombobox#itemtostring */
  itemToString?: UseComboboxProps<T>['itemToString'];
  id?: string;
  tooltipClearAllText?: string;
  /* defines the value of the item to be compared by, a unique "id" or "value" for example. deals with the case that the selected item is different in shape from item in list, but has the same value (BE\FE differences for example). */
  getItemValueToCompareBy?: (item?: T | null | undefined) => any;
  required?: 'true' | 'false' | boolean;
  onInputChange?: (inputValue: string) => void;
  dropdownVariant?: keyof typeof dropdownVariants;
  isFetching?: boolean;
  /* decides the alignment of the checkbox icon to the text */
  alignItemToCheckbox?: 'flex-start' | 'center';
  initialIsOpen?: boolean;
  showSelectedNumberOnPlaceholder?: boolean;
  noResultsMessage?: string;
  // When true (default) selected items are pulled to the top of the list. Set false to keep
  // the caller-provided item order regardless of selection.
  sortSelectedFirst?: boolean;
}

export const DEFAULT_ROW_HEIGHT = 40;

const MultiCombobox = <T,>({
  placeholder = '...',
  value,
  itemToString = item => {
    if (typeof item === 'string') {
      return item;
    }

    if (item === null) {
      return '';
    }

    // ts ignoring this because Item cannot basically have name prop when it's under Typescript generic and we dont know the type...
    // keeping this condition here for backward compatability
    //@ts-expect-error
    if (typeof item?.name === 'string') {
      //@ts-expect-error
      return item.name;
    }

    return '';
  },
  items: itemsProp,
  onChange,
  menuMode = 'absolute',
  alwaysOpen,
  isItemDisabled = () => false,
  renderItem,
  onInputChange,
  disabled,
  onItemSelect,
  active,
  isAsync,
  isFetching,
  id,
  alignItemToCheckbox = 'flex-start',
  showSelectedNumberOnPlaceholder = true,
  getItemValueToCompareBy = item => item,
  dropdownVariant = 'default',
  initialIsOpen,
  isItemSelected = (selectedItems, item) => {
    return !!selectedItems?.find(selected => isEqual(getItemValueToCompareBy(selected), getItemValueToCompareBy(item)));
  },
  required,
  noResultsMessage,
  sortSelectedFirst = true,
}: MultiComboboxProps<T>): React.JSX.Element => {
  const [inputValue, setInputValue] = React.useState<string>('');
  const [isFocused, setIsFocused] = React.useState<boolean>(false);
  // set empty list if items prop is null or undefined
  const items = itemsProp || [];

  /**
   * Multiple selection downshift logic
   * uses to handle the logic of multiple selection
   */
  const { getDropdownProps, selectedItems, setSelectedItems } = useMultipleSelection<T>({
    selectedItems: value || [],
    onSelectedItemsChange: changes => {
      setInputValue(inputValue);
      onChange?.(changes.selectedItems);
    },
  });

  /**
   * Select downshift logic
   */
  const stateReducer = (state: UseComboboxState<T>, actionAndChanges: UseComboboxStateChangeOptions<T>) => {
    const { type, changes } = actionAndChanges;

    switch (type) {
      case useCombobox.stateChangeTypes.InputClick: {
        return {
          ...changes,
          // dont close the menu on blur if "alwaysOpen" prop is true
          isOpen: alwaysOpen ? true : changes.isOpen,
        };
      }
      case useCombobox.stateChangeTypes.InputBlur: {
        return {
          ...changes,
          // dont close the menu on blur if "alwaysOpen" prop is true
          isOpen: alwaysOpen,
        };
      }
      // these cases are for keeping the menu open after clicking or selecting an item
      case useCombobox.stateChangeTypes.ItemClick:
      case useCombobox.stateChangeTypes.InputKeyDownEnter: {
        return {
          ...changes,
          isOpen: true,
          inputValue: inputValue,
          highlightedIndex: state.highlightedIndex,
        };
      }
      default:
        return changes;
    }
  };

  function getItems(search: string) {
    const filtered = items.filter(item => {
      if (isAsync) {
        return true;
      }
      return itemToString(item)
        .toLowerCase()
        .includes((search || '').toLowerCase());
    });

    if (!sortSelectedFirst) {
      return filtered;
    }

    return filtered.sort((a, b) => {
      // show selected items first
      if (isItemSelected(selectedItems, a) && !isItemSelected(selectedItems, b)) {
        return -1;
      }
      if (!isItemSelected(selectedItems, a) && isItemSelected(selectedItems, b)) {
        return 1;
      }
      return 0;
    });
  }

  const currentItems = getItems(inputValue);

  /**
   * Items
   */

  const { isOpen, getToggleButtonProps, getMenuProps, highlightedIndex, getItemProps, getInputProps } = useCombobox<T>({
    stateReducer,
    inputValue,
    items: currentItems,
    selectedItem: null,
    itemToString,
    initialIsOpen: initialIsOpen || alwaysOpen,
    isItemDisabled,
    onSelectedItemChange: changes => {
      if (isItemSelected(selectedItems, changes.selectedItem)) {
        setSelectedItems(
          selectedItems.filter(
            item => !isEqual(getItemValueToCompareBy(item), getItemValueToCompareBy(changes.selectedItem)),
          ),
        );
      } else {
        setSelectedItems([...selectedItems, changes.selectedItem] as T[]);
      }
      onItemSelect?.(changes.selectedItem);
    },
  });

  const isDisabled = disabled;

  return (
    <MultiSelectionComponentWrapper dropdownVariant={dropdownVariant} menuMode={menuMode}>
      <div {...getDropdownProps()}>
        <div className={'combobox-wrapper'}>
          <input
            aria-required={required}
            {...getInputProps({
              id,
              className: classNames({ focused: isFocused, active }),
              onBlur: () => {
                setIsFocused(false);
              },
              onFocus: () => {
                setIsFocused(true);
              },
              onKeyDown: e => {
                // if the menu is open and escape is clicked , stop event propagation in case another element listens for it
                if (e.key === 'Escape' && isOpen) {
                  e.stopPropagation();
                }
              },
              onChange: e => {
                const newValue = (e.target as HTMLInputElement).value;
                setInputValue(newValue);
                onInputChange?.(newValue);
              },
              disabled: isDisabled,
              placeholder:
                selectedItems.length && showSelectedNumberOnPlaceholder
                  ? `${placeholder} (${selectedItems.length})`
                  : placeholder,
            })}
          />

          <div className={'buttons'}>
            {isFetching ? (
              <Spinner size={'xs'} className={'spinner'} />
            ) : (
              <>
                {!alwaysOpen && !isDisabled && (
                  <RoundButton
                    {...getDropdownProps(getToggleButtonProps({ disabled: isDisabled }))}
                    variant={'secondary-clean'}
                    aria-label={isOpen ? 'Close options' : 'Open options'}
                    size={'sm'}
                  >
                    <IconAnimatedChevron isOn={!isOpen} icon={'CHEVRON_DOWN'} size={'md'} />
                  </RoundButton>
                )}
              </>
            )}
          </div>
        </div>

        <ul
          {...getMenuProps({
            className: classNames('dropdown', { open: isOpen }),
            'aria-multiselectable': true,
          })}
        >
          {isOpen && currentItems.length === 0 && noResultsMessage ? (
            <li className="menu-item disabled" role="option" aria-selected={false} aria-disabled={true}>
              <Text size={'md'}>{noResultsMessage}</Text>
            </li>
          ) : (
            isOpen &&
            currentItems.map((item, index) => {
              const isSelected = isItemSelected(selectedItems, item);

              return (
                <li
                  key={`item ${index}`}
                  className={classNames('menu-item', {
                    highlighted: highlightedIndex === index,
                    disabled: isItemDisabled(item),
                  })}
                  {...getItemProps({
                    item,
                    index,
                    'aria-selected': isSelected,
                    'aria-label': itemToString(item),
                    'data-testid': 'combobox-option',
                  })}
                >
                  <div className={classNames(`align-items-${alignItemToCheckbox}`, 'inner-item-container')}>
                    <Icon
                      aria-hidden={true}
                      className={'item-icon'}
                      name={isSelected ? 'CHECKBOX_SELECTED' : 'CHECKBOX'}
                      variant={isSelected ? 'action' : 'gray600'}
                      size={'xl'}
                    />
                    {renderItem?.(item) ? (
                      <div className={'item-text'}>{renderItem?.(item)}</div>
                    ) : (
                      <div className={'item-text'}>
                        <Text size={'md'}>{itemToString(item)}</Text>
                      </div>
                    )}
                  </div>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </MultiSelectionComponentWrapper>
  );
};

export default MultiCombobox;
